'use client';
import { Avatar } from '@ui/components/atoms/avatar';
import type { IAvatarSize } from '@ui/components/atoms/avatar';
import { formattedDateWithDateAgo } from '@ui/components/organisms/message/message.constants';
import type { IMessage } from '@ui/components/organisms/message/message.typings';
import type { ContextUserType } from '@ui/constants/context';
import { usePostHogFeatureFlag } from '@ui/hooks/posthog/usePostHogFeatureFlag';
import { BadgeCheck, PinIcon } from 'lucide-react';
import { useMemo } from 'react';
import PostItemAuthorWrap from './post-item-author-wrap';
import { DynamicRouteBuilder } from '@ui/constants/dynamic-route-builder';

interface Props {
  userType: ContextUserType;
  postOwnerUserType: ContextUserType;
  post: IMessage;
  creatorId: string;
  avatarSize?: IAvatarSize;
  IsPinned?: boolean;
  /**
   * Refering to the use new routing structure
   * e.g /creators/{creator_id}/communities/{community_id}/posts/{post_id}
   */
  isDynamicRoute?: boolean;
  communityDetails: NonNullable<IMessage['community_details']>;
  moderators_ids: string[];
  /**
   * Set to true when PostItemAuthor is rendered inside a Link component
   * to avoid nested anchor tags
   */
  isInsideLink?: boolean;
}
export const PostItemAuthor = ({
  userType,
  post,
  avatarSize = 'xs',
  creatorId,
  postOwnerUserType,
  IsPinned,
  isDynamicRoute,
  communityDetails,
  moderators_ids,
  isInsideLink = false,
}: Props) => {
  const formattedDate = useMemo(() => formattedDateWithDateAgo(post.created_at), [post.created_at]);
  const leaveFanFromCrewEnabled = usePostHogFeatureFlag('leave-fan-from-crew') === true;
  const isRemovedMember = post.membership_status === 'removed';
  const isInactiveMember =
    isRemovedMember ||
    (leaveFanFromCrewEnabled &&
      (post.inactive_member ||
        post.membership_status === 'left' ||
        post.profile_enabled === false));
  const authorName = isRemovedMember
    ? '[Removed]'
    : isInactiveMember
    ? '[Left Crew]'
    : post.user_username;
  const authorAvatar = isInactiveMember ? undefined : post.user_avatar_url;

  const url = useMemo(() => {
    if (isInactiveMember) {
      return null;
    }

    if (isDynamicRoute && postOwnerUserType === 'creator') {
      const route = DynamicRouteBuilder.creatorBySlug(
        communityDetails.creator_display_name || communityDetails.creator_id,
      );
      return route;
    }

    if (isDynamicRoute && postOwnerUserType === 'fan') {
      const route = DynamicRouteBuilder.fanBySlug(
        communityDetails.creator_display_name || communityDetails.creator_id,
        post?.user_id,
      );
      return route;
    }

    if (postOwnerUserType === 'creator' && userType === 'fan') {
      return `/creator/?id=${creatorId}&tab=Badges`;
    }

    if (userType === 'fan' && postOwnerUserType === 'fan') {
      return `/fan-profile/?fanId=${post?.user_id}&creatorId=${communityDetails.creator_id}`;
    }

    if (userType === 'creator' && postOwnerUserType === 'fan') {
      return `/fan-profile/?fanId=${post?.user_id}`;
    }
    return null;
  }, [
    isInactiveMember,
    post?.user_id,
    creatorId,
    userType,
    postOwnerUserType,
    communityDetails.creator_id,
    communityDetails.creator_display_name,
    isDynamicRoute,
  ]);

  const isCreator = useMemo(
    () => post.has_creator_account || postOwnerUserType === 'creator',
    [post.has_creator_account, postOwnerUserType],
  );

  const isModerator = useMemo(
    () => moderators_ids?.includes(post?.user_id) || false,
    [moderators_ids, post?.user_id],
  );

  return (
    <PostItemAuthorWrap url={url} isInsideLink={isInsideLink}>
      <Avatar
        size={avatarSize}
        src={authorAvatar}
        displayName={authorName}
        type={isCreator ? 'creator' : 'fan'}
        isSubscribed={!isInactiveMember && post.is_subscribed_to_creator}
      />
      <div>
        <div className="flex flex-row items-center gap-1">
          <div className="flex items-center gap-1">
            <p className="font-nunito text-sm font-semibold">{authorName}</p>
            {isCreator && <BadgeCheck className="text-primary h-4 w-4" />}
          </div>

          <span className="bg-note block h-1 w-1 rounded-full"></span>
          <p className="font-nunito text-note text-xs font-light" style={{ fontSize: 'x-small' }}>
            {formattedDate} {post?.content_updated_at && '(Edited)'}
          </p>
          {IsPinned && (
            <div className="flex items-center gap-1">
              <div className="flex items-center bg-orange-100 h-5 w-5 rounded-full justify-center border border-primaryLight">
                <PinIcon className="text-primaryActive font-thin h-3 w-3 fill-white" />
              </div>
              <span
                className="font-nunito text-primaryActive text-xs font-semibold"
                style={{ fontSize: 'x-small' }}>
                Pinned
              </span>
            </div>
          )}
        </div>

        {isCreator && (
          <p
            className="font-nunito text-note -mt-1 text-xs font-light"
            style={{ fontSize: 'x-small' }}>
            Creator
          </p>
        )}

        {isModerator && !isCreator && !isInactiveMember && (
          <p
            className="font-nunito text-note -mt-1 text-xs font-light"
            style={{ fontSize: 'x-small' }}>
            Moderator
          </p>
        )}
      </div>
    </PostItemAuthorWrap>
  );
};
